import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import {
  quoteTimeline,
  type ModalityHistoryEntry,
  type RiskChange,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatDateTime,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

/**
 * "Ver alteracoes" da cotacao (P-COT-3; GetProposalModalityDataHistories e
 * GetProposalRiskChange do OnPoint).
 *
 * Duas trilhas: os dados da modalidade (valor garantido, vigencia, prazo, inicio da
 * complementar e nome fantasia) e o risco (premio, taxa e comissao gravados). Cada
 * linha e o retrato depois da mudanca, com quem mudou; o valor que mudou em relacao a
 * linha anterior aparece destacado. Nada e calculado aqui.
 *
 * O "Reiniciar" e o "Reset" do OnPoint como marco: acrescenta uma linha com os valores
 * do momento, sem apagar nenhuma. A tela compara a partir dela.
 */
export function ChangesHistoryDialog({
  operationId,
  open,
  onClose,
}: {
  operationId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aba, setAba] = useState<'modalidade' | 'risco'>('modalidade');
  const podeReiniciar = can('operacoes.operacao.update');

  const modalidade = useQuery({
    queryKey: ['quote-timeline', operationId, 'modality-history'],
    queryFn: ({ signal }) => quoteTimeline.modalityHistory(operationId, signal),
    enabled: open,
  });
  const risco = useQuery({
    queryKey: ['quote-timeline', operationId, 'risk-changes'],
    queryFn: ({ signal }) => quoteTimeline.riskChanges(operationId, signal),
    enabled: open,
  });

  const reiniciar = useMutation({
    mutationFn: async (): Promise<void> => {
      if (aba === 'modalidade') {
        await quoteTimeline.resetModalityHistory(operationId);
      } else {
        await quoteTimeline.resetRiskChanges(operationId);
      }
    },
    onSuccess: () => {
      toast.success(
        'Marco de reinicio gravado. As linhas anteriores continuam guardadas.',
      );
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
      void queryClient.invalidateQueries({ queryKey: ['operations', operationId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const consulta = aba === 'modalidade' ? modalidade : risco;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ver alteracoes"
      description="Retrato dos valores da cotacao na abertura e depois de cada mudanca, com quem mudou."
      size="lg"
      footer={
        <>
          {podeReiniciar ? (
            <Button
              variant="secondary"
              loading={reiniciar.isPending}
              onClick={() => reiniciar.mutate()}
            >
              Reiniciar historico
            </Button>
          ) : null}
          <Button onClick={onClose}>Fechar</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div role="tablist" aria-label="Historicos" className="flex gap-2">
          <Button
            role="tab"
            aria-selected={aba === 'modalidade'}
            size="sm"
            variant={aba === 'modalidade' ? 'primary' : 'secondary'}
            onClick={() => setAba('modalidade')}
          >
            Dados da modalidade
          </Button>
          <Button
            role="tab"
            aria-selected={aba === 'risco'}
            size="sm"
            variant={aba === 'risco' ? 'primary' : 'secondary'}
            onClick={() => setAba('risco')}
          >
            Premio, taxa e comissao
          </Button>
        </div>

        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar o historico">
            {consulta.error.message}
          </Alert>
        ) : aba === 'modalidade' ? (
          <DataTable
            columns={colunasDaModalidade(modalidade.data ?? [])}
            items={[...(modalidade.data ?? [])].reverse()}
            keyOf={(item) => item.id}
            loading={modalidade.isPending}
            empty={<EmptyState icon="file" title="Nenhuma alteracao registrada" />}
          />
        ) : (
          <DataTable
            columns={colunasDoRisco(risco.data ?? [])}
            items={[...(risco.data ?? [])].reverse()}
            keyOf={(item) => item.id}
            loading={risco.isPending}
            empty={<EmptyState icon="file" title="Nenhuma alteracao registrada" />}
          />
        )}
      </div>
    </Modal>
  );
}

/** A linha anterior na ordem gravada; o marco de reinicio nao compara para tras. */
function anteriorDe<T extends { id: string; kind: string }>(
  lista: T[],
  item: T,
): T | null {
  const indice = lista.findIndex((linha) => linha.id === item.id);
  if (indice <= 0 || item.kind === 'reinicio' || item.kind === 'carga') return null;
  return lista[indice - 1] ?? null;
}

/** Valor com destaque quando mudou em relacao a linha anterior. */
function celula(texto: string, mudou: boolean): ReactNode {
  return mudou ? (
    <span className="rounded bg-warning-50 px-1 font-medium text-warning-700">
      {texto}
    </span>
  ) : (
    texto
  );
}

function colunasDaModalidade(lista: ModalityHistoryEntry[]) {
  const comparar = (
    item: ModalityHistoryEntry,
    ler: (l: ModalityHistoryEntry) => string,
  ) => {
    const anterior = anteriorDe(lista, item);
    return celula(ler(item), anterior !== null && ler(anterior) !== ler(item));
  };
  return [
    {
      key: 'quando',
      header: 'Data',
      render: (item: ModalityHistoryEntry) => formatDateTime(item.created_at),
    },
    {
      key: 'motivo',
      header: 'Registro',
      render: (item: ModalityHistoryEntry) => (
        <StatusBadge kind="historicoCotacao" value={item.kind} />
      ),
    },
    {
      key: 'is',
      header: 'Importancia Segurada',
      align: 'right' as const,
      render: (item: ModalityHistoryEntry) =>
        comparar(item, (l) => formatMoney(l.insured_amount)),
    },
    {
      key: 'inicio',
      header: 'Inicio',
      hideBelow: 'md' as const,
      render: (item: ModalityHistoryEntry) =>
        comparar(item, (l) => formatCivilDate(l.coverage_start)),
    },
    {
      key: 'fim',
      header: 'Fim',
      hideBelow: 'md' as const,
      render: (item: ModalityHistoryEntry) =>
        comparar(item, (l) => formatCivilDate(l.coverage_end)),
    },
    {
      key: 'prazo',
      header: 'Prazo (dias)',
      align: 'right' as const,
      hideBelow: 'md' as const,
      render: (item: ModalityHistoryEntry) =>
        comparar(item, (l) => (l.term_days === null ? '-' : String(l.term_days))),
    },
    {
      key: 'complementar',
      header: 'Inicio da complementar',
      hideBelow: 'lg' as const,
      render: (item: ModalityHistoryEntry) =>
        comparar(item, (l) => formatCivilDate(l.complementary_coverage_start)),
    },
    {
      key: 'fantasia',
      header: 'Nome fantasia',
      hideBelow: 'lg' as const,
      render: (item: ModalityHistoryEntry) =>
        comparar(item, (l) => l.insured_trade_name || '-'),
    },
    {
      key: 'autor',
      header: 'Autor',
      render: (item: ModalityHistoryEntry) => item.author_label || '-',
    },
  ];
}

function colunasDoRisco(lista: RiskChange[]) {
  const comparar = (item: RiskChange, ler: (l: RiskChange) => string) => {
    const anterior = anteriorDe(lista, item);
    return celula(ler(item), anterior !== null && ler(anterior) !== ler(item));
  };
  return [
    {
      key: 'quando',
      header: 'Data',
      render: (item: RiskChange) => formatDateTime(item.created_at),
    },
    {
      key: 'motivo',
      header: 'Registro',
      render: (item: RiskChange) => (
        <StatusBadge kind="historicoCotacao" value={item.kind} />
      ),
    },
    {
      key: 'premio',
      header: 'Premio',
      align: 'right' as const,
      render: (item: RiskChange) => comparar(item, (l) => formatMoney(l.premium)),
    },
    {
      key: 'taxa',
      header: 'Taxa',
      align: 'right' as const,
      render: (item: RiskChange) => comparar(item, (l) => formatRate(l.rate_bps)),
    },
    {
      key: 'comissao',
      header: 'Comissao',
      align: 'right' as const,
      render: (item: RiskChange) => comparar(item, (l) => formatRate(l.commission_bps)),
    },
    {
      key: 'is',
      header: 'Importancia Segurada',
      align: 'right' as const,
      hideBelow: 'md' as const,
      render: (item: RiskChange) =>
        comparar(item, (l) => formatMoney(l.insured_amount)),
    },
    {
      key: 'autor',
      header: 'Autor',
      render: (item: RiskChange) => item.author_label || '-',
    },
  ];
}
